// Servicio centralizado de captura de leads de WhatsApp.
//
// Cada botón/enlace que abre WhatsApp en el sitio debe pasar por
// `openWhatsApp()`. Antes de redirigir, se registra el lead en n8n -> Google
// Sheets con un envío "fire-and-forget": nunca lanza excepción ni bloquea la
// apertura de WhatsApp, aunque n8n esté caído o lento.
//
// Reemplaza a orderService.ts (que queda como wrapper de compatibilidad).

const N8N_LEAD_WEBHOOK_URL =
  process.env.REACT_APP_N8N_LEAD_WEBHOOK_URL ||
  process.env.REACT_APP_N8N_WEBHOOK_URL ||
  'https://n8n.chavasystems.com.mx/webhook/lead';

const IDEMPOTENCY_STORAGE_KEY = 'geno_lead_idempotency_key';
const UTM_STORAGE_KEY = 'geno_lead_utm';

export interface LeadPayload {
  /** Nombre del prospecto, si se conoce. */
  nombre?: string;
  /** Teléfono del prospecto, si se conoce. */
  telefono?: string;
  email?: string;
  servicio?: string;
  mensaje?: string;
  /** Identificador de la página/sección de origen (p. ej. "contacto-form"). */
  paginaOrigen?: string;
  /** Campo honeypot: si viene relleno, n8n descarta el lead (anti-bot). */
  website?: string;
}

interface Utm {
  source?: string;
  medium?: string;
  campaign?: string;
}

/** UUID v4 con fallback para navegadores sin crypto.randomUUID. */
const uuidv4 = (): string => {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

/**
 * Clave de idempotencia estable por visitante. Se persiste en localStorage para
 * que el mismo usuario reutilice la misma clave y n8n no genere duplicados
 * aunque pulse varios botones de WhatsApp.
 */
const getIdempotencyKey = (): string => {
  try {
    const existing = localStorage.getItem(IDEMPOTENCY_STORAGE_KEY);
    if (existing) return existing;
    const key = uuidv4();
    localStorage.setItem(IDEMPOTENCY_STORAGE_KEY, key);
    return key;
  } catch {
    // Modo incógnito / storage bloqueado: clave efímera por sesión.
    return uuidv4();
  }
};

/**
 * Captura los parámetros UTM de la URL y los persiste en sessionStorage, para
 * no perder la atribución de campaña al navegar por el sitio.
 */
const getUtm = (): Utm => {
  try {
    const params = new URLSearchParams(window.location.search);
    const fromUrl: Utm = {
      source: params.get('utm_source') || undefined,
      medium: params.get('utm_medium') || undefined,
      campaign: params.get('utm_campaign') || undefined,
    };
    if (fromUrl.source || fromUrl.medium || fromUrl.campaign) {
      sessionStorage.setItem(UTM_STORAGE_KEY, JSON.stringify(fromUrl));
      return fromUrl;
    }
    const stored = sessionStorage.getItem(UTM_STORAGE_KEY);
    return stored ? (JSON.parse(stored) as Utm) : {};
  } catch {
    return {};
  }
};

/** Clasifica el dispositivo a partir del userAgent. */
const getDeviceType = (): 'mobile' | 'tablet' | 'desktop' => {
  const ua = navigator.userAgent || '';
  if (/iPad|Tablet|PlayBook|Silk/i.test(ua) || (/Android/i.test(ua) && !/Mobile/i.test(ua))) {
    return 'tablet';
  }
  if (/Mobi|Android|iPhone|iPod|IEMobile|BlackBerry|Opera Mini/i.test(ua)) {
    return 'mobile';
  }
  return 'desktop';
};

/**
 * Registra el lead en n8n. Fire-and-forget: no retorna ni espera. Usa
 * `keepalive` para que el request sobreviva a la apertura de WhatsApp. Cualquier
 * error se ignora a propósito: registrar el lead nunca debe impedir el contacto.
 */
export const captureLead = (payload: LeadPayload): void => {
  if (!N8N_LEAD_WEBHOOK_URL) return;

  const utm = getUtm();
  const body = {
    idempotencyKey: getIdempotencyKey(),
    fechaRegistro: new Date().toISOString(),
    nombre: payload.nombre || '',
    telefono: payload.telefono || '',
    email: payload.email || '',
    servicio: payload.servicio || '',
    mensaje: payload.mensaje || '',
    paginaOrigen: payload.paginaOrigen || document.title || '',
    url: window.location.href,
    source: utm.source || 'directo',
    medium: utm.medium || '',
    campaign: utm.campaign || '',
    deviceType: getDeviceType(),
    userAgent: navigator.userAgent || '',
    estadoLead: 'PENDIENTE',
    // Honeypot anti-bot: si viene con contenido, n8n descarta el registro.
    website: payload.website || '',
  };

  try {
    fetch(N8N_LEAD_WEBHOOK_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      keepalive: true,
    }).catch(() => {
      /* Ignorado: el registro no debe bloquear la redirección a WhatsApp. */
    });
  } catch {
    /* Ignorado: idem. */
  }
};

/**
 * Punto único para abrir WhatsApp desde cualquier botón del sitio. Primero
 * registra el lead (no bloqueante) y luego abre WhatsApp.
 *
 * @param phone   Número en formato internacional sin "+" (p. ej. "525528447262").
 * @param mensaje Texto opcional ya en claro (se codifica internamente).
 * @param payload Datos del lead + paginaOrigen para la atribución.
 */
export const openWhatsApp = (
  phone: string,
  mensaje: string | undefined,
  payload: LeadPayload = {},
): void => {
  captureLead(payload);

  const base = `https://wa.me/${phone}`;
  const url = mensaje ? `${base}?text=${encodeURIComponent(mensaje)}` : base;
  window.open(url, '_blank', 'noopener,noreferrer');
};
